// nuolino-admin/src/auth/AuthProvider.tsx
// State machine de sessão (loading/authenticated/unauthenticated/expired/forbidden).
// Tokens vivem em cookies httpOnly (BFF), então o frontend só sabe o status + User.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { http } from '@/lib/http';
import { UnauthorizedError } from '@/lib/http-errors';
import { AuthContext } from './AuthContext';

export type UserRole = 'patient' | 'nutritionist' | 'admin';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatarUrl?: string | null;
  crn?: string | null;
  subscription?: { type: string } | null;
  subscriptionExpiresAt?: string | null;
}

export type SessionState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'unauthenticated' }
  | { status: 'expired' }
  | { status: 'forbidden'; reason: string };

export interface AuthContextValue {
  state: SessionState;
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  loginWithMock: () => Promise<void>;
  /**
   * loginWithGoogleIdToken: fluxo client-side (Google Sign-In JS gera o
   * id_token no browser, mandamos pro BFF que repassa pro Go). Substitui
   * o redirect OAuth que dependia de rotas server-side que o Go nao tem.
   */
  loginWithGoogleIdToken: (idToken: string) => Promise<void>;
  /**
   * loginWithGoogle: legacy. Apenas dispara a janela de Google Sign-In.
   * Mantida para compat com testes. Em producao, prefira o componente
   * <GoogleSignInButton> que orquestra o popup + loginWithGoogleIdToken.
   */
  loginWithGoogle: () => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface AuthProviderProps {
  children: ReactNode;
  /** Endpoint para buscar o usuário atual. Default `/api/v1/me`. */
  meEndpoint?: string;
  /** Inicialização custom (testes): pula o probe inicial. */
  skipInitialProbe?: boolean;
}

export function AuthProvider({
  children,
  meEndpoint = '/api/v1/me',
  skipInitialProbe = false,
}: AuthProviderProps) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const probe = useCallback(async (): Promise<void> => {
    try {
      const user = await http.get<AuthUser>(meEndpoint, { skipRefresh: true });
      if (!isMounted.current) return;
      setState({ status: 'authenticated', user });
    } catch (err) {
      if (!isMounted.current) return;
      if (err instanceof UnauthorizedError) setState({ status: 'expired' });
      else setState({ status: 'unauthenticated' });
    }
  }, [meEndpoint]);

  useEffect(() => {
    if (skipInitialProbe) {
      setState({ status: 'unauthenticated' });
      return;
    }
    void probe();
  }, [probe, skipInitialProbe]);

  const login = useCallback(
    async (email: string, password: string, turnstileToken?: string): Promise<void> => {
      setState({ status: 'loading' });
      try {
        await http.post<{ user: AuthUser }>(
          '/api/v1/auth/login',
          { email, password, turnstile_token: turnstileToken },
          { skipRefresh: true },
        );
        await probe();
      } catch (err) {
        if (!isMounted.current) return;
        setState({ status: 'unauthenticated' });
        throw err;
      }
    },
    [probe],
  );

  // loginWithGoogle: deprecated. Mantida para compat (testes) e como
  // stub que alerta. Em prod, use <GoogleSignInButton> + loginWithGoogleIdToken.
  const loginWithGoogle = useCallback((): void => {
    // O redirect server-side foi removido (o Go nao tem GET /auth/google
    // — so POST com id_token). Esta funcao so existe para nao quebrar
    // testes legacy; em runtime ela nao faz nada.
    if (typeof window !== 'undefined') {
      console.warn(
        '[AuthProvider] loginWithGoogle() deprecated — use <GoogleSignInButton> + loginWithGoogleIdToken()',
      );
    }
  }, []);

  // loginWithGoogleIdToken: fluxo client-side (Google Sign-In JS gera
  // o id_token no browser; mandamos pro BFF que repassa pro Go via
  // POST /api/v1/auth/google com {id_token} no body).
  const loginWithGoogleIdToken = useCallback(
    async (idToken: string): Promise<void> => {
      setState({ status: 'loading' });
      try {
        await http.post<{ user: AuthUser }>(
          '/api/v1/auth/google',
          { id_token: idToken },
          { skipRefresh: true },
        );
        await probe();
      } catch (err) {
        if (!isMounted.current) return;
        setState({ status: 'unauthenticated' });
        throw err;
      }
    },
    [probe],
  );

  // loginWithMock: chama /api/v1/auth/mock-login no BFF. Disponivel apenas
  // enquanto o BFF tem Bff__MockAuth=true (env var). Util para validar o
  // pipeline BFF (cookies httpOnly + /api/v1/me) end-to-end sem depender
  // do Go backend ter um usuario real provisionado.
  const loginWithMock = useCallback(async (): Promise<void> => {
    setState({ status: 'loading' });
    try {
      await http.post<{ user: AuthUser }>('/api/v1/auth/mock-login', undefined, {
        skipRefresh: true,
      });
      await probe();
    } catch (err) {
      if (!isMounted.current) return;
      setState({ status: 'unauthenticated' });
      throw err;
    }
  }, [probe]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await http.post('/api/v1/auth/logout', undefined, { skipRefresh: true });
    } finally {
      if (isMounted.current) setState({ status: 'unauthenticated' });
    }
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await probe();
  }, [probe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      user: state.status === 'authenticated' ? state.user : null,
      isLoading: state.status === 'loading',
      isAuthenticated: state.status === 'authenticated',
      login,
      loginWithMock,
      loginWithGoogleIdToken,
      loginWithGoogle,
      logout,
      refresh,
    }),
    [state, login, loginWithMock, loginWithGoogleIdToken, loginWithGoogle, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
